import React, { useEffect, useMemo } from "react";
import { Modal, message, Select, Input, DatePicker } from "antd";
import styled from "@emotion/styled";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { useFetch } from "hooks/useFetch";
import queryKeys from "common/queryKey";
import { currencyCodeMap } from "common/Constants";
import { ButtonOutlined, ButtonFilled } from "reusableComponent/UIButtons";
import { CALL_FEATCH_API } from "common/API";
import dayjs from "dayjs";

/* ─── Currency symbol map ─── */
const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  INR: "₹",
  AED: "د.إ",
  SGD: "S$",
  AUD: "A$",
  CAD: "C$",
  JPY: "¥",
  CHF: "Fr",
  HKD: "HK$",
  CNY: "¥",
  MYR: "RM",
  SAR: "﷼",
  QAR: "﷼",
  KWD: "KD",
  BHD: "BD",
  OMR: "﷼",
};

const CURRENCY_LABEL_MAP = {
  USD: "US Dollar",
  EUR: "Euro",
  GBP: "British Pound",
  INR: "Indian Rupee",
  AED: "UAE Dirham",
  SGD: "Singapore Dollar",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
  JPY: "Japanese Yen",
  CHF: "Swiss Franc",
  HKD: "Hong Kong Dollar",
  CNY: "Chinese Yuan",
  MYR: "Malaysian Ringgit",
  SAR: "Saudi Riyal",
  QAR: "Qatari Riyal",
  KWD: "Kuwaiti Dinar",
  BHD: "Bahraini Dinar",
  OMR: "Omani Rial",
};

/* ─── Styled Components ─── */
const ModalBody = styled.div`
  * {
    outline: none !important;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  label .required-star {
    color: #ef4444;
    margin-left: 2px;
  }

  input:not(.ant-picker-input > input),
  select,
  textarea {
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    padding: 6px 10px;
    height: 36px;
    font-size: 14px;
    color: #111827;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  input:not(.ant-picker-input > input)[readonly],
  input:not(.ant-picker-input > input):read-only {
    background: #f9fafb;
    cursor: not-allowed;
    color: #6b7280;
  }

  textarea {
    min-height: 100px;
    height: auto;
  }

  .error {
    color: #ef4444;
    font-size: 12px;
  }

  .amount-input-wrapper {
    display: flex;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
    height: 36px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .amount-prefix {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    background: #f3f4f6;
    border-right: 1px solid #d9d9d9;
    font-size: 13px;
    color: #374151;
    white-space: nowrap;
    font-weight: 500;
    min-width: fit-content;
  }

  .amount-prefix .symbol {
    font-size: 14px;
    color: #111827;
  }

  .amount-input-wrapper input {
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    flex: 1;
    height: 100%;
    padding: 0 10px;
  }

  .amount-input-wrapper input:focus {
    border: none !important;
    box-shadow: none !important;
  }

  input[type="date"] {
    cursor: pointer;
    position: relative;
  }

  input[type="date"]::-webkit-calendar-picker-indicator {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  /* Prevent global input styles from bleeding into AntD DatePicker internals */
  .ant-picker .ant-picker-input > input {
    border: none !important;
    box-shadow: none !important;
    height: auto !important;
    background: transparent !important;
    padding: 0 !important;
  }
`;

const FullDatePicker = styled(DatePicker)`
  && {
    width: 100%;
    height: 36px;
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    box-shadow: none;
    padding: 4px 11px;
    display: flex;
    align-items: center;

    &:hover {
      border-color: #d9d9d9;
      box-shadow: none;
    }

    &.ant-picker-focused {
      border-color: #00c48c;
      box-shadow: none;
    }

    &.field-error {
      border-color: #ff4d4f;
    }

    /* Kill inner input border so only the outer picker shell shows */
    .ant-picker-input {
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
      height: auto !important;
      padding: 0 !important;
    }

    .ant-picker-input > input {
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
      height: auto !important;
      padding: 0 !important;
      font-size: 14px;
      color: #333;
    }
  }
`;

/* ─── Helper: Label with red star ─── */
const Label = ({ children, required }) => (
  <label>
    {children}
    {required && <span className="required-star">*</span>}
  </label>
);

/* ─── Helper: normalize a value for loose matching ─── */
const norm = (v) => String(v ?? "").trim().toLowerCase();

const FundRequestModal = ({ visible, onClose, editRecord = null, onSuccess }) => {
  const userInfo = useSelector((store) => get(store, "common.userInfo"));
  const userEmail = userInfo?.email || userInfo?.userid || "";

  const isEdit = Boolean(editRecord);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      clientid: "",
      clientname: "",
      matterid: "",
      mattername: "",
      requestamount: "",
      duedate: "",
      description: "",
      leadattorney: "",
      requestfundtype: "",
      currency: "",
    },
  });

  const requestType = watch("requestfundtype");
  const selectedCurrency = watch("currency");

  const selectedClientId = watch("clientid");
  const selectedClientName = watch("clientname");

  /* ─── Fetch Data ─── */
  const { getAllClientList, getAccounts, getAllMatersList } = queryKeys;
  const { data: clientData } = useFetch(getAllClientList.key, getAllClientList.url);

  const clientList = useMemo(
    () => clientData?.response?.data ?? [],
    [clientData]
  );

  const { data: currencyData } = useFetch(getAccounts.key, getAccounts.url);
  const currencyList = useMemo(
    () => currencyData?.response?.data?.[0] ?? {},
    [currencyData]
  );

  const currencies = useMemo(
    () => [
      ...new Set(
        (currencyData?.response?.data ?? [])
          .map((acc) => acc.currency)
          .filter(Boolean)
      ),
    ],
    [currencyData]
  );

  const CURRENCY_OPTIONS = useMemo(
    () =>
      Object.keys(currencyList)
        .filter((key) => !["id", "date", "updated_at"].includes(key))
        .map((key) => currencyCodeMap[key])
        .filter(Boolean),
    [currencyList]
  );

  // FIX 6: getAllMatersList is a function that requires a userId argument —
  // it was previously destructured and used as if it were a plain
  // {key, url} object (like getAllClientList / getAccounts), so .key/.url
  // were both undefined and the request never fired correctly (and any
  // resulting error was swallowed by useFetch's catch handler).
  const matersQuery = getAllMatersList(userInfo?.userid || userInfo?.id || "");
  const { data: matterData } = useFetch(matersQuery.key, matersQuery.url);
  const MATTER_DATA = useMemo(
    () => matterData?.response?.data ?? [],
    [matterData]
  );

  // ── FIX 1: Filter matters to only those belonging to the selected client ──
  // FIX 5: normalized (trim + lowercase) comparisons and extra field-name
  // fallbacks, so minor casing/whitespace/naming differences between the
  // client record's id and the matter record's client-id field don't
  // silently produce an empty options list.
  const filteredMatters = useMemo(
    () =>
      MATTER_DATA.filter((m) => {
        if (!selectedClientId && !selectedClientName) return true; // show all if no client yet

        const mClientId = norm(
          m.clientid ?? m.client_id ?? m.bdo_id ?? m.companyid ?? m.company_id
        );
        const mClientName = norm(m.clientname || m.client_name || m.client);
        const targetId = norm(selectedClientId);
        const targetName = norm(selectedClientName);

        return (
          (targetId && mClientId === targetId) ||
          (targetName && mClientName === targetName)
        );
      }),
    [MATTER_DATA, selectedClientId, selectedClientName]
  );

  /* ─── Reset form on open ─── */
  useEffect(() => {
    if (!visible) return;

    if (isEdit && editRecord) {
      const clientName = editRecord.clientname || editRecord.client || "";
      const matchedClient = clientList.find(
        (c) => norm(c.clientname || c.companyregisteredname) === norm(clientName)
      );
      const resolvedClientId =
        editRecord.clientid || editRecord.bdo_id || matchedClient?.bdo_id || "";

      const matterName = editRecord.mattername || editRecord.matter || "";
      const matterId = editRecord.matterid || "";
      const matchedMatter = MATTER_DATA.find(
        (m) =>
          norm(m.matterid || m.matter_id || m.id) === norm(matterId) ||
          norm(m.mattername || m.matter_name) === norm(matterName)
      );
      const resolvedMatterId = matchedMatter
        ? String(matchedMatter.matterid || matchedMatter.matter_id || matchedMatter.id)
        : matterId;
      const resolvedMatterName =
        matchedMatter?.mattername || matchedMatter?.matter_name || matterName;
      const resolvedLeadAttorney =
        matchedMatter?.leadattorney ||
        matchedMatter?.lead_attorney ||
        editRecord.leadattorney ||
        "";

      reset({
        clientid: resolvedClientId,
        clientname: clientName,
        requestfundtype: editRecord.requestfundtype || editRecord.requesttype || "Client Account",
        matterid: resolvedMatterId,
        mattername: resolvedMatterName,
        currency: editRecord.currency || "USD",
        requestamount: editRecord.requestamount || editRecord.amount || "",
        duedate: editRecord.duedate || "",
        description: editRecord.description || "",
        leadattorney: resolvedLeadAttorney,
      });
    } else {
      reset({
        clientid: "",
        clientname: "",
        requestfundtype: "",
        matterid: "",
        mattername: "",
        currency: "",
        requestamount: "",
        duedate: "",
        description: "",
        leadattorney: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editRecord, isEdit, clientList, MATTER_DATA]);

  // NOTE (FIX 4 — root cause of "matter dropdown option not listed"):
  // The old code cleared matterid/mattername/leadattorney in a useEffect
  // that watched `selectedClientId`. That effect ran on EVERY change of
  // selectedClientId — including the change caused by reset() above when
  // the modal opens in edit mode. So immediately after reset() populated
  // matterid/mattername from the saved record, this effect wiped them out
  // again, making the previously-selected matter disappear from the field
  // (and, on re-render races, from what looked like the dropdown's options).
  //
  // Fix: only clear those fields inside the client Select's own onChange
  // handler below, so it only fires on a genuine user-driven client change,
  // never on a programmatic reset(). The standalone useEffect is removed.

  const onError = (errs) => console.log("FORM ERRORS ❌", errs);

  /* ─── Submit ─── */
  const onSubmit = async (data) => {
    try {
      const sharedFields = {
        clientid: data.clientid,
        clientname: data.clientname,
        currency: data.currency || "USD",
        requestamount: Number(data.requestamount),
        duedate: data.duedate,
        description: data.description,
      };

      const isMatter = data.requestfundtype === "Matter Fund";
      let endpoint, method, payload;

      if (isEdit) {
        endpoint = `insyts-legal/updatefundrequest`;
        method = "PATCH";
        payload = {
          ...sharedFields,
          fundid: editRecord.fundid || editRecord.id || editRecord.key,
          requestfundtype: data.requestfundtype,
          matterid: isMatter ? data.matterid || "" : "",
          mattername: isMatter ? data.mattername || "" : "",
          status: editRecord.status || "Waiting for Approval",
          leadattorney: isMatter ? data.leadattorney || "" : editRecord.leadattorney || "",
          updatedby: userEmail,
        };
      } else {
        endpoint = `insyts-legal/addfundrequest`;
        method = "POST";
        payload = {
          ...sharedFields,
          requestfundtype: data.requestfundtype,
          matterid: isMatter ? data.matterid || "" : "",
          mattername: isMatter ? data.mattername || "" : "",
          status: "Waiting for Approval",
          leadattorney: isMatter ? data.leadattorney || "" : "",
          createdby: userEmail,
        };
      }

      const response = await CALL_FEATCH_API(endpoint, method, payload);

      if (response?.status === "SUCCESS") {
        message.success(
          isEdit ? "Fund request updated successfully!" : "Fund request created successfully!"
        );
        onSuccess?.();
        onClose();
      } else {
        const errData = await response.json().catch(() => null);
        message.error(errData?.message || `Failed to ${isEdit ? "update" : "create"} request`);
      }
    } catch (err) {
      console.error("FundRequestModal submit error:", err);
      message.error("Something went wrong");
    }
  };

  /* ─── Currency display helpers ─── */
  const currencySymbol = CURRENCY_SYMBOL_MAP[selectedCurrency] || selectedCurrency;
  const currencyLabel = CURRENCY_LABEL_MAP[selectedCurrency] || selectedCurrency;

  return (
    <Modal
      title={isEdit ? "Edit Fund Request" : "Create Fund Request"}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose={false}
    >
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit, onError)}>

          {/* Row 1: Client + Request Type */}
          <div className="form-row">
            <div className="form-group">
              <Label required>Client</Label>
              <Controller
                name="clientid"
                control={control}
                rules={{ required: "Client is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select client"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(selectedId) => {
                      const selected = clientList.find((c) => c.bdo_id === selectedId);
                      field.onChange(selectedId);
                      setValue("clientname", selected?.clientname || selected?.companyregisteredname || "");

                      // FIX 4: clear dependent matter fields only on a real,
                      // user-driven client change — not on programmatic reset().
                      setValue("matterid", "");
                      setValue("mattername", "");
                      setValue("leadattorney", "");
                    }}
                    options={clientList.map((c) => ({
                      label: c.companyregisteredname || c.clientname || c.bdo_id,
                      value: c.bdo_id,
                    }))}
                  />
                )}
              />
              {errors.clientid && <span className="error">{errors.clientid.message}</span>}
            </div>

            <div className="form-group">
              <Label required>Request Type</Label>
              <Select
                value={watch("requestfundtype")}
                onChange={(val) => setValue("requestfundtype", val)}
                options={[
                  { label: "Client Account", value: "Client Account" },
                  { label: "Matter Fund", value: "Matter Fund" },
                ]}
              />
            </div>
          </div>

          {/* Row 2: Matter (conditional) + Currency OR Amount */}
          <div className="form-row">
            {requestType === "Matter Fund" ? (
              // ── FIX 1: Matter dropdown filtered by selected client ──
              <div className="form-group">
                <Label required>Matter</Label>
                <Controller
                  name="matterid"
                  control={control}
                  rules={{ required: "Matter is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={
                        !selectedClientId
                          ? "Select a client first"
                          : filteredMatters.length === 0
                            ? "No matters for this client"
                            : "Select matter"
                      }
                      disabled={!selectedClientId}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={(selectedId) => {
                        const selected = filteredMatters.find(
                          (m) => String(m.matterid || m.matter_id || m.id) === String(selectedId)
                        );
                        field.onChange(selectedId);
                        setValue("mattername", selected?.mattername || selected?.matter_name || selected?.name || "");
                        setValue("leadattorney", selected?.leadattorney || selected?.lead_attorney || selected?.attorney || "");

                        const matterCurrency =
                          selected?.currency ||
                          selected?.localcurrency ||
                          selected?.local_currency ||
                          selected?.currencycode ||
                          null;
                        if (matterCurrency) {
                          setValue("currency", matterCurrency);
                        }
                      }}
                      options={filteredMatters.map((m) => {
                        const id = m.matterid || m.matter_id || m.id;
                        const name = m.mattername || m.matter_name || m.name || id;
                        return { label: name, value: String(id) };
                      })}
                    />
                  )}
                />
                {errors.matterid && <span className="error">{errors.matterid.message}</span>}
              </div>
            ) : (
              <div className="form-group">
                <Label required>Currency</Label>
                <Controller
                  name="currency"
                  control={control}
                  rules={{ required: "Currency is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select currency"
                      disabled={!requestType}
                      onChange={(val) => field.onChange(val)}
                      options={currencies.map(c => ({ label: c, value: c }))}
                    />
                  )}
                />
                {errors.currency && <span className="error">{errors.currency.message}</span>}
              </div>
            )}

            {/* Amount */}
            <div className="form-group">
              <Label required>Amount Requesting</Label>
              <div
                className="amount-input-wrapper"
                style={{
                  opacity: (requestType === "Matter Fund" ? !!requestType : (selectedCurrency && requestType)) ? 1 : 0.5,
                  cursor: (requestType === "Matter Fund" ? !!requestType : (selectedCurrency && requestType)) ? "auto" : "not-allowed",
                }}
              >
                <span className="amount-prefix">
                  {selectedCurrency ? (
                    <>
                      <span className="symbol">{currencySymbol}</span>
                      {currencyLabel}
                    </>
                  ) : (
                    <span className="symbol">$</span>
                  )}
                </span>
                <Controller
                  name="requestamount"
                  control={control}
                  rules={{
                    required: "Amount is required",
                    min: { value: 1, message: "Amount must be greater than 0" },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="0"
                      disabled={requestType === "Matter Fund" ? !requestType : (!selectedCurrency || !requestType)}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>
              {errors.requestamount && (
                <span className="error">{errors.requestamount.message}</span>
              )}
            </div>
          </div>

          {/* ── FIX 3: Due Date — restored and placed in its own row ── */}
          <div className="form-row">
            <div className="form-group">
              <Label required>Due Date</Label>
              <Controller
                name="duedate"
                control={control}
                rules={{ required: "Due date is required" }}
                render={({ field }) => (
                  <FullDatePicker
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(dayjsVal) =>
                      field.onChange(dayjsVal ? dayjsVal.format("YYYY-MM-DD") : "")
                    }
                    format="DD MMM YYYY"
                    placeholder="Select date"
                    className={errors.duedate ? "field-error" : ""}
                    allowClear={false}
                  />
                )}
              />
              {errors.duedate && <span className="error">{errors.duedate.message}</span>}
            </div>
          </div>

          {/* Description */}
          <div className="form-group" style={{ marginBottom: 20 }}>
            <Label>Description</Label>
            <textarea {...register("description")} placeholder="Optional notes..." />
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <ButtonOutlined
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </ButtonOutlined>
            <ButtonFilled
              htmlType="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : isEdit ? "Update" : "Create"}
            </ButtonFilled>
          </div>

        </form>
      </ModalBody>
    </Modal>
  );
};

export default FundRequestModal;